import { useEffect, useState, useCallback } from 'react';

import { getNestedValue } from '../utility';

export function useForm(initialValues) {
  const [values, setValues] = useState(initialValues);
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    return () => setValues(initialValues);
  }, []);

  const focusRef = useCallback((element) => {
    if (element) element.focus();
  }, []);

  const setValue = useCallback((prop, value) => {
    if (!prop.includes('.')) {
      return setValues((prev) => ({ ...prev, [prop]: value }));
    }

    setValues((prev) => {
      let props = prop.split('.');

      let currentValue = prev;

      props.forEach((p, idx) => {
        if (idx < props.length - 1) {
          currentValue = currentValue[p];
        } else {
          currentValue[p] = value;
        }
      });

      return { ...prev };
    });
  }, []);

  const onChange = useCallback(
    (prop) => (event) => {
      let { value, type } = event.target;
      if (type === 'checkbox') {
        value = event.target.checked;
      }

      setValue(prop, value);
    },
    []
  );

  const validate = useCallback((event) => {
    if (!event) return false;

    event.preventDefault();
    const htmlForm = event.currentTarget;

    if (
      htmlForm instanceof HTMLFormElement &&
      htmlForm?.checkValidity() === false
    ) {
      event.stopPropagation();
      setValidated(true);

      let element = htmlForm.elements.item(0);
      for (var i = 1; element; element = htmlForm.elements.item(i++)) {
        if (!element?.validity?.valid) {
          element?.scrollIntoView({ behaviour: 'smooth', block: 'center' });
          element?.focus();
          break;
        }
      }
      return false;
    }

    return true;
  }, []);

  const reset = useCallback(() => setValues(initialValues), []);

  const input = useCallback(
    (property, { controlId = null, autoFocus = false } = {}) => {
      return {
        controlId: controlId || property,
        value: getNestedValue(property, values),
        onChange: onChange(property),
        ref: autoFocus ? focusRef : null
      };
    },
    [values]
  );

  const select = useCallback(
    (
      property,
      {
        controlId = null,
        options = [],
        optionLabel = (opt) => opt.label,
        optionValue = (opt) => opt.id
      } = {}
    ) => {
      return {
        controlId: controlId || property,
        options: options || [],
        getOptionLabel: optionLabel,
        getOptionValue: optionValue,
        onChange: (opt) => setValue(property, opt),
        value: getNestedValue(property, values)
      };
    },
    [values]
  );

  return {
    values,
    validated,
    focusRef,
    setValue,
    setValues,
    onChange,
    setValidated,
    validate,
    reset,
    input,
    select,
    getValue: (property) => getNestedValue(property, values)
  };
}
